import { useRef, useState } from "react";
import { toast } from "sonner";
import api, { apiErr, fileUrl } from "../lib/api";
import { downscaleImage } from "../lib/img";
import { ImagePlus, Video, Loader2, X, Star } from "lucide-react";

const MAX_VIDEO_MB = 800;
const CHUNK_SIZE = 5 * 1024 * 1024; // 5MB pieces to slip under proxy body limits

export const MediaUploader = ({ media = [], videos = [], onChange }) => {
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const imgRef = useRef(null);
  const vidRef = useRef(null);

  // Big files (video) upload in chunks so they don't hit the proxy's request-size limit.
  const uploadChunked = async (file) => {
    const uploadId = (crypto.randomUUID?.() || String(Date.now() + Math.random()));
    const total = Math.ceil(file.size / CHUNK_SIZE);
    for (let i = 0; i < total; i++) {
      const fd = new FormData();
      fd.append("upload_id", uploadId);
      fd.append("index", i);
      fd.append("total", total);
      fd.append("filename", file.name || "video.mp4");
      fd.append("content_type", file.type || "video/mp4");
      fd.append("chunk", file.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE));
      const { data } = await api.post("/upload/chunk", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setProgress(Math.round(((i + 1) / total) * 100));
      if (data.url) return data.url;
    }
    return null;
  };

  const uploadOne = async (file) => {
    if (file.type?.startsWith("image/")) {
      const toSend = await downscaleImage(file, 1600, 0.85);
      const fd = new FormData();
      fd.append("file", toSend);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      return data.url;
    }
    return uploadChunked(file); // videos & other large files go chunked
  };

  const onPick = async (e, kind) => {
    const files = Array.from(e.target.files || []);
    e.target.value = "";
    if (!files.length) return;
    if (kind === "video") {
      const tooBig = files.find((f) => f.size > MAX_VIDEO_MB * 1024 * 1024);
      if (tooBig) return toast.error(`That video is over ${MAX_VIDEO_MB}MB — please trim it a little`);
    }
    setUploading(true);
    setProgress(0);
    try {
      const urls = [];
      for (const f of files) urls.push(await uploadOne(f));
      if (kind === "image") onChange({ media: [...media, ...urls], videos });
      else onChange({ media, videos: [...videos, ...urls] });
      toast.success(`${urls.length} ${kind === "image" ? "photo" : "video"}${urls.length > 1 ? "s" : ""} added`);
    } catch (err) { toast.error(apiErr(err)); }
    finally { setUploading(false); setProgress(0); }
  };

  const removeImage = (i) => onChange({ media: media.filter((_, idx) => idx !== i), videos });
  const removeVideo = (i) => onChange({ media, videos: videos.filter((_, idx) => idx !== i) });
  const makeFeatured = (i) => {
    if (i === 0) return;
    const next = [media[i], ...media.filter((_, idx) => idx !== i)];
    onChange({ media: next, videos });
  };

  return (
    <div className="space-y-3" data-testid="media-uploader">
      {media.length > 0 && (
        <div className="grid grid-cols-3 gap-2">
          {media.map((url, i) => (
            <div key={url + i} className="relative aspect-square rounded-xl overflow-hidden group" data-testid={`media-image-${i}`}>
              <img src={fileUrl(url, 400)} alt="" className="w-full h-full object-cover" />
              {i === 0 && (
                <span className="absolute top-1 left-1 text-[10px] font-semibold bg-[var(--tenant-accent)] text-white px-1.5 py-0.5 rounded-full flex items-center gap-0.5" data-testid="media-featured-badge">
                  <Star className="h-2.5 w-2.5" fill="currentColor" /> Featured
                </span>
              )}
              {i !== 0 && (
                <button type="button" onClick={() => makeFeatured(i)} title="Set as featured"
                  data-testid={`media-feature-btn-${i}`}
                  className="absolute top-1 left-1 h-6 w-6 rounded-full bg-black/50 text-white flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/70">
                  <Star className="h-3 w-3" />
                </button>
              )}
              <button type="button" onClick={() => removeImage(i)} title="Remove"
                data-testid={`media-remove-image-${i}`}
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/50 text-white flex items-center justify-center hover:bg-black/70">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      {videos.length > 0 && (
        <div className="grid grid-cols-2 gap-2">
          {videos.map((url, i) => (
            <div key={url + i} className="relative rounded-xl overflow-hidden bg-black" data-testid={`media-video-${i}`}>
              <video src={fileUrl(url)} className="w-full aspect-video object-cover" muted />
              <button type="button" onClick={() => removeVideo(i)} title="Remove"
                data-testid={`media-remove-video-${i}`}
                className="absolute top-1 right-1 h-6 w-6 rounded-full bg-black/60 text-white flex items-center justify-center hover:bg-black/80">
                <X className="h-3 w-3" />
              </button>
            </div>
          ))}
        </div>
      )}

      <div className="flex items-center gap-2">
        <button type="button" onClick={() => imgRef.current?.click()} disabled={uploading}
          data-testid="media-add-photos-btn"
          className="flex-1 flex items-center justify-center gap-2 text-sm rounded-xl border border-dashed p-3 hover:bg-muted/40 transition-colors disabled:opacity-60">
          {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4 accent-text" />} Add photos
        </button>
        <button type="button" onClick={() => vidRef.current?.click()} disabled={uploading}
          data-testid="media-add-video-btn"
          className="flex-1 flex items-center justify-center gap-2 text-sm rounded-xl border border-dashed p-3 hover:bg-muted/40 transition-colors disabled:opacity-60">
          <Video className="h-4 w-4 accent-text" /> {uploading && progress > 0 ? `Uploading ${progress}%` : "Add video"}
        </button>
      </div>
      {media.length > 1 && <p className="text-[11px] text-muted-foreground">Tap the ★ on any photo to make it the featured image partners see first.</p>}
      <input ref={imgRef} type="file" accept="image/*" multiple className="hidden" onChange={(e) => onPick(e, "image")} data-testid="media-image-input" />
      <input ref={vidRef} type="file" accept="video/*" multiple className="hidden" onChange={(e) => onPick(e, "video")} data-testid="media-video-input" />
    </div>
  );
};

export default MediaUploader;
